/**
 * ステージ内容ビューア(docs/10 §5 Phase 1)。
 *
 * 左: セクション見出し付きのステージ一覧(検索付き)
 * 右: 選択したステージの設定 + 単語一覧
 */
import { useMemo, useState } from 'react';
import {
  CATEGORY_LABELS,
  groupBySection,
  searchStages,
  type ContentWord,
  type LanguageContent,
  type StageDefinition,
} from '../services/content';
import type { RequestTarget } from './RequestDialog';
import { WordTable } from './WordTable';

interface Props {
  readonly content: LanguageContent;
  readonly assetsBase: string | null;
  readonly onRequest: (target: RequestTarget) => void;
}

/** 解放条件の説明文。 */
function unlockText(stage: StageDefinition): string {
  const unlock = stage.unlock;
  if (!unlock || (unlock.requires.length === 0 && unlock.minStars === 0)) return '最初から解放';
  const parts: string[] = [];
  if (unlock.requires.length > 0) parts.push(`前提: ${unlock.requires.join(', ')}`);
  if (unlock.minStars > 0) parts.push(`星 ${String(unlock.minStars)} 以上`);
  return parts.join(' / ');
}

function quizText(stage: StageDefinition): string {
  const { mode, repeatCount, timeLimit, questionLimit } = stage.quiz;
  const extra = [
    repeatCount === undefined ? null : `くり返し ${String(repeatCount)}`,
    timeLimit === undefined ? null : `制限 ${String(timeLimit)}秒`,
    questionLimit === undefined ? null : `出題 ${String(questionLimit)}問`,
  ].filter((value): value is string => value !== null);
  return extra.length > 0 ? `${mode}(${extra.join(' / ')})` : mode;
}

export function StageBrowser({ content, assetsBase, onRequest }: Props) {
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(content.stages[0]?.id ?? null);

  const visible = useMemo(() => searchStages(content.stages, query), [content.stages, query]);
  const sections = useMemo(() => groupBySection(visible), [visible]);
  const stage = useMemo(
    () => content.stages.find((candidate) => candidate.id === selectedId) ?? null,
    [content.stages, selectedId],
  );

  // 参照切れ(wordIds にあるのに単語セットに無い)は管理画面で見えるようにする。
  const { words, missingIds } = useMemo(() => {
    if (!stage) return { words: [] as ContentWord[], missingIds: [] as string[] };
    const found: ContentWord[] = [];
    const missing: string[] = [];
    for (const id of stage.wordIds) {
      const word = content.wordsById.get(id);
      if (word) found.push(word);
      else missing.push(id);
    }
    return { words: found, missingIds: missing };
  }, [stage, content.wordsById]);

  /** 解放連鎖: このステージを前提にしている後続。 */
  const unlockedBy = useMemo(() => {
    if (!stage) return [];
    return content.stages
      .filter((candidate) => candidate.unlock?.requires.includes(stage.id) === true)
      .map((candidate) => candidate.id);
  }, [content.stages, stage]);

  return (
    <div className="workspace">
      <div className="pane pane--list">
        <div className="toolbar">
          <input
            className="input"
            type="search"
            placeholder="ステージ名・ID で検索"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
        </div>
        <p className="muted mono">
          {String(visible.length)} / {String(content.stages.length)} ステージ
        </p>
        {sections.map((group) => (
          <div key={`${group.category}-${group.section}`}>
            <h3 className="section-title">
              {CATEGORY_LABELS[group.category]} — {group.section}
            </h3>
            {group.stages.map((candidate) => (
              <button
                key={candidate.id}
                type="button"
                className={`stage-item${candidate.id === selectedId ? ' stage-item--active' : ''}`}
                onClick={() => {
                  setSelectedId(candidate.id);
                }}
              >
                <div className="stage-item__title">{candidate.title}</div>
                <div className="stage-item__meta">
                  <span className="mono">{candidate.id}</span> ・{String(candidate.wordIds.length)}
                  語
                </div>
              </button>
            ))}
          </div>
        ))}
      </div>

      <div className="pane">
        {stage === null ? (
          <p className="muted">左の一覧からステージを選んでください。</p>
        ) : (
          <>
            <div className="detail__head">
              <h2>{stage.title}</h2>
              <span className="muted">{stage.titleSub ?? ''}</span>
              <span className="mono muted">{stage.id}</span>
              <button
                className="button button--small"
                type="button"
                onClick={() => {
                  onRequest({
                    type: 'stage',
                    id: stage.id,
                    label: stage.title,
                    language: stage.language,
                    defaultKind: 'stage-composition',
                  });
                }}
              >
                このステージに修正メモ
              </button>
            </div>

            <div className="meta-grid">
              <div>
                <span className="meta-grid__key">カテゴリ: </span>
                {CATEGORY_LABELS[stage.category]}
              </div>
              <div>
                <span className="meta-grid__key">セクション: </span>
                {stage.section}
              </div>
              <div>
                <span className="meta-grid__key">語数: </span>
                {String(stage.wordIds.length)}
              </div>
              <div>
                <span className="meta-grid__key">出題: </span>
                {quizText(stage)}
              </div>
              <div>
                <span className="meta-grid__key">画像表示: </span>
                {stage.viewImage ? 'あり' : 'なし'}
              </div>
              <div>
                <span className="meta-grid__key">音声: </span>
                {stage.voice ? 'あり' : 'なし'}
              </div>
              <div>
                <span className="meta-grid__key">星コイン: </span>
                {stage.starCoins.join(' / ')}
              </div>
              <div>
                <span className="meta-grid__key">ゲーム種別: </span>
                {stage.gameType}
              </div>
              <div>
                <span className="meta-grid__key">解放条件: </span>
                {unlockText(stage)}
              </div>
              <div>
                <span className="meta-grid__key">次に解放: </span>
                {unlockedBy.length === 0 ? '—' : unlockedBy.join(', ')}
              </div>
            </div>

            <WordTable
              words={words}
              missingIds={missingIds}
              assetsBase={assetsBase}
              onRequest={(word) => {
                onRequest({
                  type: 'word',
                  id: word.id,
                  label: word.display,
                  language: stage.language,
                  defaultKind: 'meaning',
                });
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}
